import { DownOutlined, UpOutlined } from '@ant-design/icons';
import { Checkbox, Col, Row, Tag, Typography } from 'antd';
import dayjs from 'dayjs';
import { OrderStatusTagColor } from 'constants/status';
import React, { useMemo } from 'react';
import { splitIntoColumns } from './prepSheetLayout';

function PrepSheetOrderCard({
  order,
  isChecked,
  onToggleIngredient,
  progress,
  expanded,
  onToggleExpanded,
}) {
  const ingredients = order.ingredients || [];
  const items = order.items || [];
  const ingredientCount = ingredients.length;
  const progressPercent = progress.total > 0
    ? Math.round((progress.checked / progress.total) * 100)
    : 0;

  const ingredientColumns = useMemo(
    () => splitIntoColumns(ingredients),
    [ingredients],
  );

  const renderIngredient = (ingredient) => {
    const checked = isChecked(ingredient.ingredient_id);

    return (
      <div
        key={`${order.order_id}-${ingredient.ingredient_id}`}
        className="prep-sheet-checklist-row"
      >
        <div className="prep-sheet-checklist-check-cell">
          <Checkbox
            checked={checked}
            onChange={(event) => onToggleIngredient(ingredient.ingredient_id, event.target.checked)}
            className="prep-sheet-checklist-checkbox prep-sheet-screen-checkbox"
            aria-label={`Mark ${ingredient.name} as prepped`}
          />
          <span className="prep-sheet-print-checkbox" aria-hidden />
        </div>
        <span className="prep-sheet-checklist-name">{ingredient.name}</span>
        <span className="prep-sheet-checklist-qty">
          {ingredient.quantity}
          {' '}
          {ingredient.unit}
        </span>
      </div>
    );
  };

  const customerName = order.customer_name || 'Guest';
  const locationName = order.location || '';

  return (
    <div className={`prep-sheet-order-card${expanded ? ' prep-sheet-order-card--expanded' : ''}`}>
      <button
        type="button"
        className="prep-sheet-order-card-summary prep-sheet-no-print"
        onClick={onToggleExpanded}
        aria-expanded={expanded}
      >
        <div className="prep-sheet-order-card-summary-main">
          <div className="prep-sheet-order-card-meta">
            <div className="prep-sheet-order-card-header-left">
              <Typography.Text className="prep-sheet-order-id">
                #
                {order.order_number || order.order_id}
              </Typography.Text>
              <Tag color={OrderStatusTagColor[order.status_id] || 'default'} className="prep-sheet-status-tag">
                {(order.status || '').toUpperCase()}
              </Tag>
              <Typography.Text type="secondary" className="prep-sheet-order-datetime">
                {dayjs(order.date_time).format('ddd, MMM D · h:mm A')}
              </Typography.Text>
            </div>
            <div className="prep-sheet-order-card-summary-customer">
              <Typography.Text strong className="prep-sheet-summary-customer-name">
                {customerName}
              </Typography.Text>
              {locationName && (
                <Typography.Text type="secondary" className="prep-sheet-summary-location">
                  {locationName}
                </Typography.Text>
              )}
            </div>
          </div>
          <div className="prep-sheet-order-summary-right">
            <div className="prep-sheet-order-progress">
              <Typography.Text type="secondary" className="prep-sheet-progress-label">
                PROGRESS
              </Typography.Text>
              <Typography.Text strong className="prep-sheet-progress-count">
                {progress.checked}
                {' / '}
                {progress.total}
              </Typography.Text>
              <div className="prep-sheet-progress-track">
                <div
                  className="prep-sheet-progress-fill"
                  style={{ width: `${progressPercent}%` }}
                />
              </div>
            </div>
            <span className="prep-sheet-expand-icon" aria-hidden>
              {expanded ? <UpOutlined /> : <DownOutlined />}
            </span>
          </div>
        </div>
      </button>

      <div className="prep-sheet-order-card-body">
        <div className="prep-sheet-print-card-header prep-sheet-print-only">
          <Typography.Text className="prep-sheet-order-id">
            #
            {order.order_number || order.order_id}
            {' '}
            ·
            {' '}
            {(order.status || '').toUpperCase()}
            {' '}
            ·
            {' '}
            {dayjs(order.date_time).format('ddd, MMM D · h:mm A')}
          </Typography.Text>
          <Typography.Title level={4} className="prep-sheet-customer-name">
            {customerName}
          </Typography.Title>
          {locationName && (
            <Typography.Text type="secondary">{locationName}</Typography.Text>
          )}
        </div>

        <Row gutter={[24, 16]} className="prep-sheet-order-body">
          <Col xs={24} lg={6}>
            <div className="prep-sheet-ordered-section">
              <Typography.Text className="prep-sheet-section-label">
                WHAT&apos;S BEING ORDERED
              </Typography.Text>
              {items.length === 0 ? (
                <Typography.Text type="secondary">No items</Typography.Text>
              ) : (
                <ul className="prep-sheet-ordered-list">
                  {items.map((item) => (
                    <li key={`${order.order_id}-${item.item_id || item.name}`}>
                      <Typography.Text strong>
                        {item.quantity}
                        x
                        {' '}
                        {item.name}
                      </Typography.Text>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </Col>
          <Col xs={24} lg={18}>
            <div className="prep-sheet-checklist-section">
              <Typography.Text className="prep-sheet-section-label">
                PREP CHECKLIST
              </Typography.Text>
              {ingredientCount === 0 ? (
                <Typography.Text type="secondary">No recipe ingredients on any item.</Typography.Text>
              ) : (
                <>
                  <div className="prep-sheet-checklist-grid prep-sheet-checklist-screen">
                    {ingredientColumns.map((column, columnIndex) => (
                      <div
                        key={`${order.order_id}-col-${columnIndex}`}
                        className="prep-sheet-checklist-column"
                      >
                        {column.map(renderIngredient)}
                      </div>
                    ))}
                  </div>
                  <div className="prep-sheet-checklist-print-flow prep-sheet-print-only">
                    {ingredients.map(renderIngredient)}
                  </div>
                </>
              )}
            </div>
          </Col>
        </Row>
      </div>
    </div>
  );
}

export default PrepSheetOrderCard;
